import React, { useEffect, useState, useCallback, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth as firebaseAuth } from "firebaseClient";
import { ParsedToken, signInWithCustomToken } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import { Box, Typography, Paper } from "@mui/material";

import { useAuth } from "providers/Auth";
import { useGlobalConfig } from "providers/GlobalConfig";
import themes from "config/themes";

import { GlobalConfigType } from "types/configs";
import { UploadProduct } from "types/products";
import { updateUserConfig } from "models/configs";
import { ONBOARDING_STEPS, SETUP_STEPS } from "types/onboarding";

import Stepper from "../../sharedComponents/Stepper";
import ProductUploadModal from "../../sharedComponents/ProductUploadModal";
import { EmailVerify } from "./components/EmailVerify";
import ResourceCreationModal from "./components/ResourceCreationModal";
import { CompanyNameEdit } from "./components/CompanyNameEdit";
import { ProductCatalogSection } from "./components/ProductCatalogSection";
import { SaveAndContinueButton } from "pages/SelfServe/sharedComponents/SaveAndContinueButton";
import { HelpButton } from "pages/SelfServe/sharedComponents/HelpButton";

import {
  createTenantResource,
  setupProductCatalog,
  finalizeOnboarding,
  TenantCreationContext,
  waitForCasesCreation,
  TenantCreationError,
  TenantCreationResult,
} from "pages/SelfServe/steps/verification/helpers/createNewUserTenant";

import { useSelfServeAlert } from "pages/SelfServe/utils/useSelfServeAlert";
import { AuthStatus } from "types/auth";

const PENDING_KEY = "sigil_pending_tenant_creation";
const CREATING_KEY = "sigil_is_creating_resources";
const PROGRESS_KEY = "sigil_creation_progress";
interface PendingCreationState {
  tenantId: string;
  userEmail: string;
  displayName: string;
  companyName: string;
}

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

const Verification: React.FC = () => {
  const { auth, refreshClaims, setOnboardingMode } = useAuth()!;

  const { globalConfig } = useGlobalConfig()! as GlobalConfigType;
  const navigate = useNavigate();
  const getFirebaseUser = () => firebaseAuth.currentUser;

  const [products, setProducts] = useState<UploadProduct[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);

  const [isCreatingResources, setIsCreatingResources] = useState<boolean>(
    () => sessionStorage.getItem(CREATING_KEY) === "true",
  );
  const [progressStep, setProgressStep] = useState<number>(() =>
    parseInt(sessionStorage.getItem(PROGRESS_KEY) ?? "0", 10),
  );
  const [err, setErr] = useState<string | null>(null);
  const [failedStep, setFailedStep] = useState<number | null>(null);
  const [canContinueToPortal, setCanContinueToPortal] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const { sendAlert } = useSelfServeAlert();

  const globalConfigRef = useRef(globalConfig);
  useEffect(() => {
    globalConfigRef.current = globalConfig;
  }, [globalConfig]);

  const authRef = useRef(auth);
  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  const creationStartedRef = useRef(false);

  const setAndPersistProgressStep = useCallback(async (step: number) => {
    sessionStorage.setItem(PROGRESS_KEY, String(step));
    setProgressStep(step);
    await nextFrame();
  }, []);

  const clearOnboardingSessionState = useCallback(() => {
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(CREATING_KEY);
    sessionStorage.removeItem(PROGRESS_KEY);

    setIsCreatingResources(false);
    setOnboardingMode(ONBOARDING_STEPS.none);
  }, [setOnboardingMode]);

  const onCall = useCallback(
    async (args: {
      email: string;
      displayName: string;
      tenantDisplayName: string;
      isSelfServe?: boolean;
    }) => {
      const functions = getFunctions();
      const bootstrapUser = httpsCallable(functions, "https-userOnCall");
      const response = await bootstrapUser(args);
      return (response?.data ?? {}) as TenantCreationResult;
    },
    [],
  );

  const handleUpdateProduct = (
    index: number,
    updatedProduct: UploadProduct,
  ) => {
    setProducts((prev) => {
      const next = [...prev];
      next[index] = updatedProduct;
      return next;
    });
  };

  const handleDeleteProduct = (index: number) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const checkEmailVerification = async () => {
    setIsCheckingVerification(true);
    try {
      if (!getFirebaseUser()) {
        setErr("Session expired. Please sign in again.");
        return;
      }

      await getFirebaseUser()?.getIdToken(true);
      const token = await getFirebaseUser()?.getIdTokenResult(true);
      const isVerified = (token?.claims as ParsedToken)?.email_verified;

      if (isVerified) {
        setShowEmailVerification(false);
        await handleConfirm({ skipEmailCheck: true });
      } else {
        setErr(
          "Email not yet verified. Please check your inbox and click the verification link.",
        );
      }
    } catch (error) {
      console.error("Error checking verification:", error);
      setErr("Unable to check verification status. Please try again.");
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const continueWithResourceCreation = useCallback(
    async (context: TenantCreationContext) => {
      setIsCreatingResources(true);
      await setAndPersistProgressStep(1);
      setErr(null);
      setFailedStep(null);
      setCanContinueToPortal(false);
      setWarnings([]);
      try {
        const currentUserEmail =
          globalConfigRef.current.user?.user ?? context.userEmail;
        await sendAlert({
          customerName: currentUserEmail,
          step: ONBOARDING_STEPS.finalizing,
        });
      } catch (emailError) {
        console.error("Failed to send alert email:", emailError);
      }

      try {
        await setAndPersistProgressStep(2);

        const catalogResult = await setupProductCatalog(
          context.tenantId,
          products,
        );
        if (!catalogResult.success) {
          setWarnings((prev) => [
            ...prev,
            `Product catalog: ${catalogResult.error}`,
          ]);
        }

        await setAndPersistProgressStep(3);

        const casesResult = await waitForCasesCreation(context.tenantId);
        if (!casesResult.success) {
          setWarnings((prev) => [...prev, `Cases: ${casesResult.error}`]);
        }

        await setAndPersistProgressStep(4);

        await finalizeOnboarding(
          authRef.current,
          globalConfigRef.current,
          firebaseAuth,
        );

        await setAndPersistProgressStep(5);
        setCanContinueToPortal(true);

        navigate("/Gating/New");
      } catch (error) {
        console.error("Failed to create resources:", error);

        if (error instanceof TenantCreationError) {
          setErr(error.userMessage);
          setFailedStep(error.step);
          console.error(`Step ${error.step} failed: ${error.technicalMessage}`);
        } else {
          setErr(
            error instanceof Error
              ? error.message
              : "Failed to create tenant. Please try again.",
          );
        }
      } finally {
        clearOnboardingSessionState();
      }
    },
    [
      products,
      sendAlert,
      navigate,
      setAndPersistProgressStep,
      clearOnboardingSessionState,
    ],
  );

  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_KEY);

    if (!raw) return;
    if (auth.status !== AuthStatus.READY) return;
    if (creationStartedRef.current) return;

    let pending: PendingCreationState;
    try {
      pending = JSON.parse(raw) as PendingCreationState;
    } catch {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }

    const claimsTenants = auth.claims?.tenant as string[] | string | undefined;

    const tenantMatches = Array.isArray(claimsTenants)
      ? claimsTenants.includes(pending.tenantId)
      : claimsTenants === pending.tenantId;

    if (!tenantMatches) {
      console.log(
        `Claims not ready yet. Expected tenant: ${pending.tenantId}, got: ${JSON.stringify(claimsTenants)}`,
      );
      return;
    }

    creationStartedRef.current = true;
    sessionStorage.setItem(CREATING_KEY, "true");
    setIsCreatingResources(true);
    void continueWithResourceCreation(pending);
  }, [auth.status, auth.claims?.tenant, continueWithResourceCreation]);

  const handleContinueToPortal = () => {
    setOnboardingMode(ONBOARDING_STEPS.none);
    navigate("/Gating/New");
  };

  const handleConfirm = async (options?: { skipEmailCheck?: boolean }) => {
    setErr(null);

    if (!options?.skipEmailCheck && !getFirebaseUser()?.emailVerified) {
      setShowEmailVerification(true);
      return;
    }

    if (!getFirebaseUser() || !globalConfig.user) {
      setErr("Session expired. Please sign in again.");
      return;
    }

    setIsConfirmLoading(true);

    const { user } = globalConfig;
    const onboarding = user.props?.onboarding;

    const baseContext: TenantCreationContext = {
      tenantId: onboarding?.survey?.tenant || "",
      userEmail: user.user,
      displayName: getFirebaseUser()?.displayName || "",
      companyName: onboarding?.survey?.companyName || "",
    };

    try {
      const res = await createTenantResource(baseContext, onCall);
      if (res instanceof TenantCreationError) {
        setErr(res.userMessage);
        return;
      }

      const { tenantId, customToken } = res;

      const pendingState: PendingCreationState = {
        tenantId,
        userEmail: baseContext.userEmail,
        displayName: baseContext.displayName,
        companyName: baseContext.companyName,
      };

      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingState));

      setOnboardingMode(ONBOARDING_STEPS.finalizing);
      await signInWithCustomToken(firebaseAuth, customToken);

      await getFirebaseUser()?.getIdToken(true);
      await refreshClaims({ forceRefresh: true });
    } catch (error) {
      console.error("Failed to create tenant:", error);
      setOnboardingMode(ONBOARDING_STEPS.none);
      setErr(
        error instanceof Error
          ? error.message
          : "Failed to create tenant. Please try again.",
      );
    } finally {
      setIsConfirmLoading(false);
    }
  };

  // pull onboarding data into UI
  useEffect(() => {
    if (!globalConfig.user) return;
    const onboarding = globalConfig.user.props?.onboarding;
    setCompanyName(onboarding?.survey?.companyName || "");
    setProducts(onboarding?.productCatalog || []);
  }, [globalConfig.user]);

  const handleCompanyNameSave = async (newName: string) => {
    const updatedConfig = {
      props: {
        onboarding: {
          survey: { companyName: newName },
        },
      },
    };
    await updateUserConfig(auth, globalConfig, updatedConfig);
    setCompanyName(newName);
  };

  const handleProductsAccept = async (nextProducts: UploadProduct[]) => {
    try {
      const updatedConfig = {
        props: {
          onboarding: {
            productCatalog: nextProducts,
          },
        },
      };

      await updateUserConfig(auth, globalConfig, updatedConfig);
      setProducts(nextProducts);
    } catch (error) {
      console.error("Failed to update product catalog:", error);
    }
  };

  if (showEmailVerification) {
    return (
      <EmailVerify
        setShowEmailVerification={setShowEmailVerification}
        checkEmailVerification={checkEmailVerification}
        isCheckingVerification={isCheckingVerification}
        err={err}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Stepper currentStep={3} />

      <Box
        sx={{
          flex: 1,
          padding: { xs: 3, md: "16px 48px" },
          backgroundColor: themes.palette.neutral.main,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Paper elevation={3} sx={{ p: 5, width: "100%", borderRadius: 2 }}>
          <Box sx={{ mb: 5 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 1, fontSize: 32 }}
            >
              Does this look right?
            </Typography>
            <HelpButton />
            <Typography variant="body1" color="text.secondary">
              We're building your profile with the following information.
            </Typography>
          </Box>

          <CompanyNameEdit
            companyName={companyName}
            onSave={handleCompanyNameSave}
          />

          <ProductCatalogSection
            products={products}
            onUpdate={handleUpdateProduct}
            onDelete={handleDeleteProduct}
            onReUpload={() => setModalOpen(true)}
          />

          <SaveAndContinueButton
            buttonText="Confirm"
            onConfirm={handleConfirm}
            loading={isConfirmLoading}
            disabled={
              isCreatingResources ||
              isConfirmLoading ||
              products.length === 0 ||
              !companyName.trim()
            }
          />
        </Paper>
      </Box>

      <ProductUploadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAccept={handleProductsAccept}
      />

      <ResourceCreationModal
        open={isCreatingResources}
        currentStep={progressStep}
        steps={SETUP_STEPS}
        error={err}
        failedStep={failedStep}
        canContinueToPortal={canContinueToPortal}
        warnings={warnings}
        onContinue={handleContinueToPortal}
      />
    </Box>
  );
};

export default Verification;
